import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import AppShell from "@/components/AppShell";
import PublicDashboard from "@/pages/PublicDashboard";
import Login from "@/pages/Login";
import Overview from "@/pages/Overview";
import AdminElections from "@/pages/AdminElections";
import AdminHierarchy from "@/pages/AdminHierarchy";
import AdminUsers from "@/pages/AdminUsers";
import AgentEntry from "@/pages/AgentEntry";
import Approvals from "@/pages/Approvals";
import AuditLog from "@/pages/AuditLog";
import About from "@/pages/About";
import PlacePage from "@/pages/PlacePage";
import IncidentReports from "@/pages/IncidentReports";
import IncidentMap from "@/pages/IncidentMap";
import FraudDashboard from "@/pages/FraudDashboard";
import BroadcastSms from "@/pages/BroadcastSms";
import Timeline from "@/pages/Timeline";
import Playbooks from "@/pages/Playbooks";
import ResetPassword from "@/pages/ResetPassword";
import SupportChat from "@/components/SupportChat";
import { InstallPrompt } from "@/components/InstallPrompt";

function Protected({ roles }) {
  const { user } = useAuth();
  if (user === undefined) return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-zinc-500 text-xs uppercase tracking-[0.3em]">Authenticating...</div>;
  if (!user) return <Navigate to="/login" replace />;
  const home = user.role === "agent" ? "/app/entry" : "/app";
  if (roles && !roles.includes(user.role)) return <Navigate to={home} replace />;
  return <AppShell><Outlet /></AppShell>;
}

// Wraps a bare page (no AppShell) and forces sign-in. Anonymous visitors are sent to /login.
function RequireLogin({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-zinc-500 text-xs uppercase tracking-[0.3em]">Authenticating...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="top-right" richColors />
        <Routes>
          <Route path="/" element={<PublicDashboard />} />
          <Route path="/place/:id" element={<PlacePage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route element={<Protected />}>
            <Route path="/app/entry" element={<AgentEntry />} />
            <Route path="/app/approvals" element={<Approvals />} />
            <Route path="/app/incidents" element={<IncidentReports />} />
            <Route path="/app/incidents/map" element={<IncidentMap />} />
            <Route path="/app/help" element={<About />} />
          </Route>
          <Route element={<Protected roles={["admin", "observer"]} />}>
            <Route path="/app" element={<Overview />} />
            <Route path="/app/fraud" element={<FraudDashboard />} />
            <Route path="/app/timeline" element={<Timeline />} />
          </Route>
          <Route element={<Protected roles={["admin"]} />}>
            <Route path="/app/broadcast" element={<BroadcastSms />} />
            <Route path="/app/playbooks" element={<Playbooks />} />
            <Route path="/app/elections" element={<AdminElections />} />
            <Route path="/app/hierarchy" element={<AdminHierarchy />} />
            <Route path="/app/users" element={<AdminUsers />} />
            <Route path="/app/audit" element={<AuditLog />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <SupportChat />
        <InstallPrompt />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
